import { useMemo, useState } from "react";
import { Anchor, Badge, Box, Card, Drawer, Group, Loader, NumberInput, ScrollArea, Select, SimpleGrid, Stack, Table, Text, Title, UnstyledButton } from "@mantine/core";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  allergenLabels,
  defaultQuantity,
  fmtNumber,
  foodsFromResult,
  groupMeals,
  groupNutrients,
  hasMealStructure,
  itemAmount,
  itemName,
  macrosFor,
  nutrientProfilePer100g,
  portionLabel,
  portionsFor,
  portionWeightG,
  startingPortion,
  titleCase,
  unitLabel,
  type AmountHint,
  type IngredientExtractOutput,
  type NewPassioFood,
  type OutgoingSearchResult,
  type PassioFood,
  type PassioFoodResult,
} from "@passio/api-client";
import { passio } from "../lib/clients";
import { useClientHeaders } from "../lib/clientSettings";
import { useSession } from "../lib/session";
import { MacroBar, MacroChips, MacroLegend } from "../theme/components";
import { ErrorNotice, FoodIcon } from "./common";
import { ErrorBoundary } from "./ErrorBoundary";

/** One search result / extracted item row. */
export function FoodRow({ item, onClick, right, sub }: { item: Partial<IngredientExtractOutput> & OutgoingSearchResult; onClick?: () => void; right?: React.ReactNode; sub?: React.ReactNode }) {
  const p = item.nutritionPreview;
  const name = itemName(item);
  const matched = item.ingredientName && item.displayName && item.ingredientName.toLowerCase() !== item.displayName.toLowerCase() ? item.displayName : null;
  const amount = itemAmount(item);
  const body = (
    <Group gap="sm" wrap="nowrap" align="flex-start" p={8} style={{ borderRadius: 10 }}>
      <FoodIcon iconId={item.iconId} src={item.images?.product || undefined} alt="" />
      <Stack gap={3} style={{ flex: 1, minWidth: 0 }}>
        <Group gap={6} wrap="nowrap">
          <Text fw={650} truncate>
            {item.ingredientName ? titleCase(item.ingredientName) : name}
          </Text>
          {item.brandName && (
            <Text fz="xs" c="dimmed" truncate>
              {item.brandName}
            </Text>
          )}
        </Group>
        {(matched || amount) && (
          <Text fz="xs" c="dimmed" truncate>
            {amount}
            {amount && matched ? " · " : ""}
            {matched ? `matched: ${matched}` : ""}
          </Text>
        )}
        {p && (
          <Group gap={8} wrap="wrap">
            <MacroChips calories={p.calories} protein={p.protein} carbs={p.carbs} fat={p.fat} size="xs" />
          </Group>
        )}
        {sub}
      </Stack>
      {right}
    </Group>
  );
  return onClick ? (
    <UnstyledButton onClick={onClick} className="pd-tile" style={{ borderRadius: 10, display: "block", width: "100%" }}>
      {body}
    </UnstyledButton>
  ) : (
    body
  );
}

const flagLabels: [keyof IngredientExtractOutput, string][] = [
  ["isRecipe", "recipe"],
  ["isSupplement", "supplement"],
  ["isSpecificBrandedProduct", "branded"],
  ["isSeveralFoodsCombined", "combined"],
];

/** Extracted items grouped into meals > components (GI Coach behaviour), or a flat list. */
export function MealGroupsView({ items, onSelect, emptyText = "No foods found.", fallbackMealName }: { items: IngredientExtractOutput[]; onSelect?: (item: IngredientExtractOutput) => void; emptyText?: string; fallbackMealName?: string }) {
  if (!items.length) return <Text c="dimmed">{emptyText}</Text>;
  const structured = hasMealStructure(items);
  const meals = groupMeals(items, { fallbackMealName });
  return (
    <Stack gap="md">
      {meals.map((meal) => {
        const realComponents = meal.components.filter((c) => !c.isOther).length;
        return (
          <Card key={meal.key}>
            <Group justify="space-between" align="flex-start" mb="sm" wrap="wrap">
              <div>
                <Title order={3} fz={20}>
                  {meal.name}
                </Title>
                <Group gap={6} mt={4}>
                  {structured && (
                    <Badge variant="light" color="violet">
                      {meal.kind}
                    </Badge>
                  )}
                  {realComponents > 0 && (
                    <Badge variant="outline" color="gray">
                      {realComponents} component{realComponents > 1 ? "s" : ""}
                    </Badge>
                  )}
                  <Badge variant="outline" color="gray">
                    {meal.components.reduce((n, c) => n + c.items.length, 0)} items
                  </Badge>
                </Group>
              </div>
              {meal.totals.hasNutrition && (
                <Stack gap={6} align="flex-end">
                  <MacroChips calories={meal.totals.calories} protein={meal.totals.protein} carbs={meal.totals.carbs} fat={meal.totals.fat} grams={meal.totals.grams} />
                  <Box w={200}>
                    <MacroBar protein={meal.totals.protein} carbs={meal.totals.carbs} fat={meal.totals.fat} />
                  </Box>
                </Stack>
              )}
            </Group>
            <Stack gap="sm">
              {meal.components.map((c) => (
                <Box key={c.key}>
                  {!(meal.components.length === 1 && c.isOther) && (
                    <Group justify="space-between" mb={2} px={8}>
                      <Text fz="sm" fw={700} c={c.isOther ? "dimmed" : undefined}>
                        {c.name}
                      </Text>
                      {c.totals.hasNutrition && (
                        <Text fz="xs" c="dimmed" ff="monospace">
                          {fmtNumber(c.totals.calories)} kcal
                        </Text>
                      )}
                    </Group>
                  )}
                  <Stack gap={2}>
                    {c.items.map(({ item, index }) => {
                      const flags = flagLabels.filter(([k]) => item[k]).map(([, l]) => l);
                      return (
                        <FoodRow
                          key={index}
                          item={item}
                          onClick={onSelect && item.refCode ? () => onSelect(item) : undefined}
                          sub={
                            flags.length || item.preparationMethods?.length ? (
                              <Group gap={4}>
                                {flags.map((f) => (
                                  <Badge key={f} size="xs" variant="dot" color="violet">
                                    {f}
                                  </Badge>
                                ))}
                                {item.preparationMethods?.map((m) => (
                                  <Badge key={m} size="xs" variant="outline" color="gray" tt="none">
                                    {m}
                                  </Badge>
                                ))}
                              </Group>
                            ) : undefined
                          }
                        />
                      );
                    })}
                  </Stack>
                </Box>
              ))}
            </Stack>
          </Card>
        );
      })}
    </Stack>
  );
}

type DetailSource = { refCode: string; data?: undefined } | { refCode?: undefined; data: PassioFood | NewPassioFood };

/** Full nutrition for a refCode (hydrated) or an already-loaded food, with portion scaling. */
export function FoodDetailPanel(props: DetailSource & { fallbackName?: string; amount?: AmountHint | null; onLeave?: () => void }) {
  const headers = useClientHeaders();
  const { toolEnabled } = useSession();
  const q = useQuery({
    queryKey: ["food", props.refCode, headers.toHeaders()],
    queryFn: async () => (await passio.foods.byRefCode(props.refCode!)).data,
    enabled: Boolean(props.refCode),
    staleTime: 5 * 60_000,
  });
  const source = props.refCode ? q.data : props.data;
  const { header, foods } = useMemo(() => foodsFromResult(source), [source]);
  const start = useMemo(() => startingPortion(portionsFor(header, foods), props.amount), [header, foods, props.amount]);
  const portions = start.portions;
  const [pi, setPi] = useState<number | null>(null);
  const [qty, setQty] = useState<number | null>(null);

  if (props.refCode && q.isLoading) {
    return (
      <Group p="xl" justify="center">
        <Loader color="violet" />
      </Group>
    );
  }
  if (q.error) return <ErrorNotice error={q.error} onRetry={() => q.refetch()} />;
  if (!header) return <Text c="dimmed">No details for this food.</Text>;

  const index = Math.min(pi ?? start.index, portions.length - 1);
  const portion = portions[index];
  const quantity = qty ?? (pi === null ? start.quantity : defaultQuantity(portion));
  const grams = portionWeightG(portion) * quantity;
  const profile = nutrientProfilePer100g(foods);
  const m = macrosFor(profile, grams);
  const f0 = foods[0] as PassioFood | undefined;
  const name = (header as PassioFoodResult).displayName || (header as PassioFood).name || props.fallbackName || "Food";
  const brandInfo = f0?.branded;
  const tags = [...new Set([...(f0?.tags ?? [])])];
  const allergens = allergenLabels(tags);
  const images = (header as PassioFoodResult).images ?? f0?.images;
  const groups = groupNutrients(profile);

  return (
    <Stack gap="lg">
      <Group gap="md" wrap="nowrap" align="flex-start">
        <FoodIcon iconId={(header as PassioFood).iconId} src={images?.product || undefined} size={72} />
        <Stack gap={4} style={{ minWidth: 0 }}>
          <Title order={2} fz={24}>
            {name}
          </Title>
          <Group gap={6}>
            {brandInfo?.owner && <Badge color="ink.8">{brandInfo.owner}</Badge>}
            {f0?.qualityScore && (
              <Badge variant="light" color={f0.qualityScore === "High" ? "lime.8" : f0.qualityScore === "Medium" ? "yellow" : "orange"}>
                {f0.qualityScore} quality
              </Badge>
            )}
            {foods.length > 1 && (
              <Badge variant="light" color="violet">
                Recipe · {foods.length} ingredients
              </Badge>
            )}
            {brandInfo?.productCode && (
              <Badge variant="outline" color="gray" ff="monospace">
                {brandInfo.productCode}
              </Badge>
            )}
          </Group>
          {images?.brand && <img src={images.brand} alt="brand" style={{ height: 28, objectFit: "contain", alignSelf: "flex-start" }} />}
        </Stack>
      </Group>

      <Card bg="white">
        <Group gap="sm" align="flex-end" wrap="wrap" mb="md">
          <Select
            label="Portion"
            data={portions.map((p, i) => ({ value: String(i), label: portionLabel(p) }))}
            value={String(index)}
            onChange={(v) => {
              setPi(Number(v ?? 0));
              setQty(null);
            }}
            allowDeselect={false}
            w={280}
          />
          <NumberInput label="Quantity" value={quantity} onChange={(v) => setQty(Number(v) || 0)} min={0} step={portion?.name === "g" ? 10 : 0.5} decimalScale={2} w={120} />
          <Text c="dimmed" fz="sm" pb={8}>
            = {fmtNumber(grams, 1)} g
          </Text>
          {props.refCode && toolEnabled("nutrition_math") && (
            <Anchor
              component={Link}
              to={`/nutrition-math?refCode=${encodeURIComponent(props.refCode)}&name=${encodeURIComponent(name)}`}
              onClick={props.onLeave}
              fz="xs"
              pb={10}
              ml="auto"
            >
              How is this calculated? See the code
            </Anchor>
          )}
        </Group>
        <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="sm">
          {[
            ["Calories", fmtNumber(m.calories), "kcal"],
            ["Protein", fmtNumber(m.protein, 1), "g"],
            ["Carbs", fmtNumber(m.carbs, 1), "g"],
            ["Fat", fmtNumber(m.fat, 1), "g"],
          ].map(([l, v, u]) => (
            <Box key={l} p="sm" style={{ background: "var(--pd-cream)", borderRadius: 12 }}>
              <Text fz={26} fw={750} lh={1.1}>
                {v}
                <Text span fz="sm" c="dimmed" fw={500}>
                  {" "}
                  {u}
                </Text>
              </Text>
              <Text fz="xs" c="dimmed">
                {l}
              </Text>
            </Box>
          ))}
        </SimpleGrid>
        <Stack gap={6} mt="md">
          <MacroBar protein={m.protein} carbs={m.carbs} fat={m.fat} />
          <Group justify="space-between">
            <MacroLegend />
            <Text fz="xs" c="dimmed">
              Fiber {fmtNumber(m.fiber, 1)} g · Sugar {fmtNumber(m.sugar, 1)} g · Sodium {fmtNumber(m.sodium)} mg
            </Text>
          </Group>
        </Stack>
      </Card>

      {(brandInfo?.ingredients || foods.length > 1 || allergens.length > 0 || tags.length > 0) && (
        <Card bg="white">
          <Stack gap="sm">
            {brandInfo?.ingredients && (
              <div>
                <Text fw={700} fz="sm">
                  Ingredients
                </Text>
                <Text fz="sm">{brandInfo.ingredients}</Text>
              </div>
            )}
            {foods.length > 1 && (
              <div>
                <Text fw={700} fz="sm" mb={4}>
                  Recipe ingredients
                </Text>
                <Group gap={6}>
                  {foods.map((f, i) => (
                    <Badge key={i} variant="outline" color="gray" tt="none">
                      {(f as PassioFood).name}
                    </Badge>
                  ))}
                </Group>
              </div>
            )}
            {allergens.length > 0 && (
              <div>
                <Text fw={700} fz="sm" mb={4}>
                  Allergens
                </Text>
                <Group gap={6}>
                  {allergens.map((a) => (
                    <Badge key={a} color="orange" variant="light" tt="none">
                      ⚠ {a}
                    </Badge>
                  ))}
                </Group>
              </div>
            )}
            {tags.length > 0 && (
              <div>
                <Text fw={700} fz="sm" mb={4}>
                  Tags
                </Text>
                <Group gap={4}>
                  {tags.map((t) => (
                    <Badge key={t} size="sm" variant="light" color="gray" tt="none">
                      {t}
                    </Badge>
                  ))}
                </Group>
              </div>
            )}
            {f0?.concerns?.length ? (
              <Text fz="xs" c="dimmed">
                Concern codes: {f0.concerns.join(", ")} (see Reference)
              </Text>
            ) : null}
          </Stack>
        </Card>
      )}

      <Card bg="white">
        <Text fw={700} mb="sm">
          Full nutrients for {fmtNumber(grams, 1)} g
        </Text>
        {groups.length === 0 ? (
          <Text c="dimmed" fz="sm">
            No detailed nutrients for this food.
          </Text>
        ) : (
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="lg">
            {groups.map((g) => (
              <div key={g.group}>
                <Text fz="sm" fw={700} c="dimmed" mb={4}>
                  {g.group}
                </Text>
                <Table fz="sm" verticalSpacing={4}>
                  <Table.Tbody>
                    {g.entries.map((e) => {
                      const v = (e.amount * grams) / 100;
                      return (
                        <Table.Tr key={`${e.name}|${e.unit}`}>
                          <Table.Td>{e.name}</Table.Td>
                          <Table.Td ta="right" ff="monospace">
                            {fmtNumber(v, v < 10 ? 2 : 1)} {unitLabel(e.unit)}
                          </Table.Td>
                        </Table.Tr>
                      );
                    })}
                  </Table.Tbody>
                </Table>
              </div>
            ))}
          </SimpleGrid>
        )}
      </Card>
    </Stack>
  );
}

/** `amount` opens the panel at the amount the clicked row showed instead of the default serving. */
export function FoodDetailDrawer({ refCode, data, name, amount, onClose }: { refCode?: string | null; data?: PassioFood | NewPassioFood | null; name?: string; amount?: AmountHint | null; onClose: () => void }) {
  const opened = Boolean(refCode || data);
  return (
    <Drawer opened={opened} onClose={onClose} position="right" size={620} title={<Text fw={700}>Food details</Text>} scrollAreaComponent={ScrollArea.Autosize}>
      <ErrorBoundary key={refCode ?? name} label="Food details" inspector>
        {refCode ? <FoodDetailPanel refCode={refCode} fallbackName={name} amount={amount} onLeave={onClose} /> : data ? <FoodDetailPanel data={data} fallbackName={name} amount={amount} /> : null}
      </ErrorBoundary>
    </Drawer>
  );
}
